import React from 'react';
import { db } from '../../../../constants/firebaseModular';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import PhoneInput from 'react-phone-input-2';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart } from '../../../../types/Chart/Chart';
import useChart from '../../../../hooks/useChart';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { NavForward } from '../../../../components/buttons/NavForward';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { createEmptyFirestoreDoc } from '../../../../data/createEmptyFirestoreDoc';
import { getUnattestedScript } from '../../../../data/treatment/getUnattestedScript';
import { Button } from '../../../../components/buttons/Button';
import { Heading } from '../../../../components/Heading';
import { doc, deleteDoc } from 'firebase/firestore';
// import { DatePicker } from 'antd';
import { formatDateForCalendar } from '../../../../utilities/formatDateForCalendar';
import { formatDateForStorage } from '../../../../utilities/formatDateForStorage';
import { differenceInYears } from 'date-fns';
import { FieldInfo } from '../../../../components/FieldInfo';
import * as Sentry from '@sentry/browser';
import Checkbox from '../../../../components/forms/Checkbox';

import { PhotoDimensions } from '../../../../types/Camera/PhotoDimensions';
import { PhotoCamera } from '../../../../components/PhotoCamera';
import { FileTypes } from '../../../../types/Files/FileTypes';
import { TreatmentOptions } from '../../../../types/Treatment/treatmentOptions';

export const Route = createFileRoute('/_authenticated/$patientId/statement/attestation')({
  component: AttestationPage,
});

function AttestationPage() {
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [scripHasError, setScripHasError] = React.useState(false);
  const [attested, setAttested] = React.useState(false);
  const [hasReader, setHasReader] = React.useState(false);
  const [readerSubmitted, setReaderSubmitted] = React.useState(false);
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);
  const [takingPhoto, setTakingPhoto] = React.useState({
    readerIdentificationFront: false,
  });
  const [localCode, setLocalCode] = React.useState<string | undefined>(undefined);
  const { mutate } = useUpdateChartSection();

  React.useEffect(() => {
    if (chart && chart.SELECTED_TREATMENT_OPTION && chart.SELECTED_TREATMENT_OPTION.code?.length > 0) {
      setLocalCode(chart.SELECTED_TREATMENT_OPTION.code);
    } else {
      setLocalCode('fullCode');
    }
  }, [chart]);

  // SET HAS READER FROM CHART
  React.useEffect(() => {
    if (chart?.SCRIPT_REVIEW?.speaker === 'reader' || chart?.SCRIPT_REVIEW?.speaker === 'hca') {
      setHasReader(true);
    }
  }, [chart]);

  const handleRemoveReader = async () => {
    if (!chart) {
      // setAttested(false);
      return;
    }
    // DELETE SELECTED_TREATMENT_OPTION DOC TO FIX BUG WHERE PATIENT CANNOT ATTEST TO SCRIPT AFTER CHANGES
    await deleteDoc(doc(db, 'users', patientId, 'Chart', 'SELECTED_TREATMENT_OPTION'));
    mutate(
      {
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: localCode as keyof typeof TreatmentOptions,
            scriptAttested: chart.SCRIPT_REVIEW.scriptAttested,
            speaker: 'patient',
            reader: {
              email: '',
              fullName: '',
              phoneNumber: '',
            },
            readerIdentificationFrontURI: '',
            telePrompterScript:
              chart.SELECTED_TREATMENT_OPTION?.unAttestedScript || chart.SCRIPT_REVIEW?.telePrompterScript,
          },
        },
      },
      {
        onSuccess: async () => {
          // alert("Successfully updated document");
          setHasReader(!hasReader);
          await deleteDoc(doc(db, 'users', patientId, 'Chart', 'SELECTED_TREATMENT_OPTION'));
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const handleAddReader = async () => {
    if (!chart) {
      // setAttested(false);
      return;
    }
    // DELETE SELECTED_TREATMENT_OPTION DOC TO FIX BUG WHERE PATIENT CANNOT ATTEST TO SCRIPT AFTER CHANGES
    // await deleteDoc(doc(db, 'users', patientId, 'Chart', 'SELECTED_TREATMENT_OPTION'));

    mutate(
      {
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: localCode as keyof typeof TreatmentOptions,
            scriptAttested: chart.SCRIPT_REVIEW.scriptAttested,
            speaker: 'reader',
            reader: {
              email: addReaderForm.state.values.email,
              fullName: addReaderForm.state.values.fullName,
              phoneNumber: addReaderForm.state.values.phoneNumber,
            },
            readerIdentificationFrontURI: chart.SCRIPT_REVIEW?.readerIdentificationFrontURI
              ? chart.SCRIPT_REVIEW?.readerIdentificationFrontURI
              : '',
            telePrompterScript: getUnattestedScript({
              code: localCode as keyof typeof TreatmentOptions,
              chart,
              patientInfo,
            }),
          },
        },
      },
      {
        onSuccess: async () => {
          // alert("Successfully updated document");
          // setHasReader(!hasReader);
          await deleteDoc(doc(db, 'users', patientId, 'Chart', 'SELECTED_TREATMENT_OPTION'));
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );

    mutate(
      {
        patientId: patientId,
        docData: {
          SELECTED_TREATMENT_OPTION: {
            code: localCode as keyof typeof TreatmentOptions,
            description: chart.SELECTED_TREATMENT_OPTION?.description,
            unAttestedScript: getUnattestedScript({
              code: localCode as keyof typeof TreatmentOptions,
              chart,
              patientInfo,
            }),
          },
        },
      },
      {
        onSuccess: async () => {
          // alert("Successfully updated document");
          // setHasReader(!hasReader);
          await deleteDoc(doc(db, 'users', patientId, 'Chart', 'SELECTED_TREATMENT_OPTION'));
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE ADD READER FORM
  const addReaderForm = useForm({
    defaultValues: {
      email: chart?.SCRIPT_REVIEW?.reader?.email ? chart.SCRIPT_REVIEW?.reader?.email : '',
      fullName: chart?.SCRIPT_REVIEW?.reader?.fullName ? chart.SCRIPT_REVIEW?.reader?.fullName : '',
      phoneNumber: chart?.SCRIPT_REVIEW?.reader?.phoneNumber ? chart.SCRIPT_REVIEW?.reader?.phoneNumber : '',
      readerIdentificationFrontURI: chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI
        ? chart.SCRIPT_REVIEW?.readerIdentificationFrontURI
        : '',
    },
    onSubmit: () => {
      handleAddReader();
    },
  });

  const handleReaderCheckboxClick = async () => {
    if (hasReader) {
      await handleRemoveReader();
      setHasReader(false);
    } else {
      setHasReader(true);
    }
  };

  // SET SELECTED_TREATMENT_OPTION TO FULL CODE FOR SELF-GUIDED PATIENTS IF IT DOESN'T EXIST
  React.useEffect(() => {
    if (patientInfo) {
      if (patientInfo.navType.base === 'self') {
        if (chart && !chart.SELECTED_TREATMENT_OPTION) {
          mutate(
            {
              patientId: patientId,
              docData: {
                SELECTED_TREATMENT_OPTION: {
                  code: localCode as keyof typeof TreatmentOptions,
                  description: 'Full Code - All Treatment & No Limitations in Care',
                  unAttestedScript: getUnattestedScript({
                    code: 'fullCode',
                    chart: chart,
                    patientInfo,
                  }),
                },
              },
            },
            {
              onSuccess: () => {
                // alert("Successfully updated document");
              },
              onError: (error) => {
                Sentry.captureException(error);
                alert(`Error updating document: ${error.message}`); // TELL THE USER
                console.error(error); // GET THE STACKTRACE
              },
            },
          );
        }
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  // WRITE SCRIPT ERROR FORM DATA TO FIRESTORE
  const handleScriptError = async () => {
    // CLONE CHART SO THAT IT CAN BE MODIFIED WITH FORM DATA (THEN SUBMITTED TO GENERATE NEW SCRIPTS)
    const chartClone = { ...chart } as Chart;
    chartClone.PATIENT_INFO_1.firstName = scriptErrorForm.state.values.firstName;
    chartClone.PATIENT_INFO_1.middleInitial = scriptErrorForm.state.values.middleInitial;
    chartClone.PATIENT_INFO_1.lastName = scriptErrorForm.state.values.lastName;
    chartClone.PATIENT_INFO_1.DOB = formatDateForStorage(scriptErrorForm.state.values.DOB);
    chartClone.HEALTHCARE_AGENT_INFO.agent = scriptErrorForm.state.values.agent;
    chartClone.HEALTHCARE_AGENT_INFO.agentToPatient = scriptErrorForm.state.values.agentToPatient;
    chartClone.HEALTHCARE_AGENT_INFO.cellPhone = scriptErrorForm.state.values.agentCellPhone;
    chartClone.HEALTHCARE_AGENT_INFO.patientToAgent = chart.HEALTHCARE_AGENT_INFO.patientToAgent;

    // UPDATE PATIENT_INFO_1 FIRESTORE DOCUMENT WITH FORM DATA
    mutate(
      {
        patientId: patientId,
        docData: {
          PATIENT_INFO_1: {
            firstName: scriptErrorForm.state.values.firstName,
            middleInitial: scriptErrorForm.state.values.middleInitial,
            gender: chart.PATIENT_INFO_1.gender ? chart.PATIENT_INFO_1.gender : '',
            lastName: scriptErrorForm.state.values.lastName,
            DOB: formatDateForStorage(scriptErrorForm.state.values.DOB),
            selfReporting: chart.PATIENT_INFO_1.selfReporting ? chart.PATIENT_INFO_1.selfReporting : true,
          },
        },
      },
      {
        onSuccess: () => {},
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );

    // UPDATE HEALTHCARE_AGENT_INFO FIRESTORE DOCUMENT WITH FORM DATA
    mutate(
      {
        patientId: patientId,
        docData: {
          HEALTHCARE_AGENT_INFO: {
            agent: scriptErrorForm.state.values.agent,
            street: chart.HEALTHCARE_AGENT_INFO.street,
            city: chart.HEALTHCARE_AGENT_INFO.city,
            state: chart.HEALTHCARE_AGENT_INFO.state,
            ZIP: chart.HEALTHCARE_AGENT_INFO.ZIP,
            country: chart.HEALTHCARE_AGENT_INFO.country,
            email: chart.HEALTHCARE_AGENT_INFO.email,
            cellPhone: scriptErrorForm.state.values.agentCellPhone,
            homePhone: chart.HEALTHCARE_AGENT_INFO.homePhone,
            agentToPatient: scriptErrorForm.state.values.agentToPatient,
            patientToAgent: chart.HEALTHCARE_AGENT_INFO.patientToAgent,
            hasPowerOfAttorney: chart?.HEALTHCARE_AGENT_INFO?.hasPowerOfAttorney
              ? chart.HEALTHCARE_AGENT_INFO.hasPowerOfAttorney
              : 'unset',
          },
        },
      },
      {
        onSuccess: async () => {
          setScripHasError(false);
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );

    // UPDATE SELECTED_TREATMENT_OPTION FIRESTORE DOCUMENT WITH NEW SCRIPT BASED ON NEW FORM DATA
    mutate(
      {
        patientId: patientId,
        docData: {
          SELECTED_TREATMENT_OPTION: {
            code: localCode as keyof typeof TreatmentOptions,
            description: chart?.SELECTED_TREATMENT_OPTION?.description
              ? chart.SELECTED_TREATMENT_OPTION.description
              : 'Full Code - All Treatment & No Limitations in Care',
            unAttestedScript: getUnattestedScript({
              code: 'fullCode',
              chart: chartClone,
              patientInfo,
            }),
          },
        },
      },
      {
        onSuccess: async () => {
          setScripHasError(false);
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );

    // UPDATE SCRIPT_REVIEW FIRESTORE DOCUMENT WITH NEW SCRIPT BASED ON NEW FORM DATA TO KEEP THEM IN SYNC (FAILURE TO DO RESULTS IN BLOCKING ABILITY TO ATTEST USING CHECKBOX)
    mutate(
      {
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: localCode as keyof typeof TreatmentOptions,
            scriptAttested: false,
            speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
            telePrompterScript: getUnattestedScript({
              code: 'fullCode',
              chart: chartClone,
              patientInfo,
            }),
          },
        },
      },
      {
        onSuccess: async () => {
          setScripHasError(false);
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE SCRIPT ERROR FORM
  const scriptErrorForm = useForm({
    defaultValues: {
      firstName: chart?.PATIENT_INFO_1?.firstName ? chart.PATIENT_INFO_1?.firstName : '',
      middleInitial: chart?.PATIENT_INFO_1?.middleInitial ? chart.PATIENT_INFO_1?.middleInitial : '',
      lastName: chart?.PATIENT_INFO_1?.lastName ? chart.PATIENT_INFO_1?.lastName : '',
      DOB: chart?.PATIENT_INFO_1?.DOB ? formatDateForCalendar(chart?.PATIENT_INFO_1?.DOB) : '',
      agent: chart?.HEALTHCARE_AGENT_INFO?.agent ? chart.HEALTHCARE_AGENT_INFO?.agent : '',
      agentToPatient: chart?.HEALTHCARE_AGENT_INFO?.agentToPatient ? chart.HEALTHCARE_AGENT_INFO?.agentToPatient : '',
      agentCellPhone: chart?.HEALTHCARE_AGENT_INFO?.cellPhone ? chart.HEALTHCARE_AGENT_INFO?.cellPhone : '',
    },
    onSubmit: () => {
      handleScriptError();
    },
  });

  // SET READER SUBMITTED FROM CHART
  React.useEffect(() => {
    if (
      chart?.SCRIPT_REVIEW?.reader &&
      chart.SCRIPT_REVIEW?.reader.email?.length > 0 &&
      chart.SCRIPT_REVIEW?.reader.fullName?.length > 0 &&
      chart.SCRIPT_REVIEW?.reader.phoneNumber?.length > 0 &&
      chart.SCRIPT_REVIEW?.reader.email == addReaderForm?.state.values.email &&
      chart.SCRIPT_REVIEW?.reader.fullName == addReaderForm?.state.values.fullName &&
      chart.SCRIPT_REVIEW?.reader.phoneNumber == addReaderForm?.state.values.phoneNumber
    ) {
      setReaderSubmitted(true);
    }
  }, [chart, addReaderForm]);

  // CREATE FULL CODE SCRIPT REVIEW DOC IN FIRESTORE FOR A SELF-GUIDED PATIENT
  const handleGenerateScript = React.useCallback(() => {
    if (!chart) {
      // setAttested(false);
      console.error('Exiting due to lack of chart');
      return;
    } else if (
      chart &&
      chart.PATIENT_INFO_1 &&
      chart.LIVING_WILL &&
      chart.HEALTHCARE_AGENT_INFO &&
      patientInfo?.navType?.base === 'self'
    ) {
      const newScript = getUnattestedScript({ code: 'fullCode', chart, patientInfo });
      // if (newScript == chart.SCRIPT_REVIEW?.telePrompterScript) {
      if (newScript == chart.SELECTED_TREATMENT_OPTION?.unAttestedScript) {
        // IF THE SCRIPT GENERATED WITH THE NEW CHART IS THE SAME, DO NOTHING
        return;
      } else {
        mutate(
          {
            patientId: patientId,
            docData: {
              SCRIPT_REVIEW: {
                attestedCode: 'fullCode',
                scriptAttested: false,
                telePrompterScript: newScript,
                speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
              },
            },
          },
          {
            onSuccess: () => {
              // alert("Successfully updated document");
              setScripHasError(false);
            },
            onError: (error) => {
              Sentry.captureException(error);
              alert(`Error updating document: ${error.message}`); // TELL THE USER
              console.error(error); // GET THE STACKTRACE
            },
          },
        );
      }
    } else {
      console.error('Exiting due to lack of patient info or chart');
      return;
    }
  }, [chart, patientInfo, patientId, mutate]);

  // CREATE EMPTY PATIENT SIGNATURE DOC IN FIRESTORE
  const handleInitializeEmptyPatientSignature = async () => {
    await createEmptyFirestoreDoc({ patientId, title: 'PATIENT_SIGNATURE' });
  };

  // INITIALIZE EMPTY PATIENT_SIGNATURE FIRESTORE DOC IF IT DOESN'T EXIST
  React.useEffect(() => {
    const docRef = chart?.PATIENT_SIGNATURE;
    if (chart && !docRef) {
      handleInitializeEmptyPatientSignature();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  // CREATE EMPTY WITNESS SIGNATURE DOC IN FIRESTORE
  const handleInitializeEmptyWitnessSignature = async () => {
    await createEmptyFirestoreDoc({ patientId, title: 'WITNESS_SIGNATURE' });
  };

  // INITIALIZE EMPTY WITNESS_SIGNATURE FIRESTORE DOC IF IT DOESN'T EXIST
  React.useEffect(() => {
    const docRef = chart?.WITNESS_SIGNATURE;
    if (chart && !docRef) {
      handleInitializeEmptyWitnessSignature();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'attestation', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'attestation',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  // SET THE SCRIPT FOR A SELF-GUIDED PATIENT
  React.useEffect(() => {
    if (chart && patientInfo?.navType?.base === 'self' && !chart.SELECTED_TREATMENT_OPTION?.code) {
      handleGenerateScript();
    }
  }, [chart, handleGenerateScript, patientInfo]);

  const handleAttestationCheckboxClick = async () => {
    if (!chart) {
      // setAttested(false);
      return;
    }
    if (patientInfo?.navType?.base === 'self') {
      // DELETE SELECTED_TREATMENT_OPTION DOC TO FIX BUG WHERE PATIENT CANNOT ATTEST TO SCRIPT AFTER CHANGES
      await deleteDoc(doc(db, 'users', patientId, 'Chart', 'SELECTED_TREATMENT_OPTION'));
      mutate(
        {
          patientId: patientId,
          docData: {
            SCRIPT_REVIEW: {
              attestedCode: localCode as keyof typeof TreatmentOptions,
              scriptAttested: !attested,
              speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
              telePrompterScript: getUnattestedScript({
                code: localCode as keyof typeof TreatmentOptions,
                chart,
                patientInfo,
              }),
              // chart.SELECTED_TREATMENT_OPTION?.unAttestedScript || chart.SCRIPT_REVIEW?.telePrompterScript,
            },
          },
        },
        {
          onSuccess: async () => {
            // alert("Successfully updated document");
            // await deleteDoc(doc(db, 'users', patientId, 'Chart', 'SELECTED_TREATMENT_OPTION'));
          },
          onError: (error) => {
            Sentry.captureException(error);
            alert(`Error updating document: ${error.message}`); // TELL THE USER
            console.error(error); // GET THE STACKTRACE
          },
        },
      );
    } else {
      mutate(
        {
          patientId: patientId,
          docData: {
            SCRIPT_REVIEW: {
              attestedCode: localCode as keyof typeof TreatmentOptions,
              scriptAttested: !attested,
              speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
              telePrompterScript: chart.SELECTED_TREATMENT_OPTION.unAttestedScript
                ? chart.SELECTED_TREATMENT_OPTION.unAttestedScript
                : 'There was an error reading your script. Please consult your concierge.',
            },
          },
        },
        {
          onSuccess: async () => {
            // alert("Successfully updated document");
          },
          onError: (error) => {
            Sentry.captureException(error);
            alert(`Error updating document: ${error.message}`); // TELL THE USER
            console.error(error); // GET THE STACKTRACE
          },
        },
      );
    }
  };

  React.useEffect(() => {
    if (chart && chart.SCRIPT_REVIEW) {
      setAttested(chart.SCRIPT_REVIEW.scriptAttested);
    }
  }, [chart]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="attestation-page" className="flex flex-col items-center justify-center">
          <Heading id="attestation-page-header" textContent="Attestation Page" />
          <p className="text-xl">
            The box below contains the script you will read when recording your MIDEO safety statement. Please read it
            in its entirety before attesting.
          </p>
          <div id="attestation-page-main" className="mt-4 flex flex-row justify-around">
            <div id="attestation-page-content" className="flex flex-col">
              <>
                <div id="script-container" className="w-full p-4 text-2xl outline">
                  <p className="text-pretty">
                    {chart.SCRIPT_REVIEW?.telePrompterScript || chart.SELECTED_TREATMENT_OPTION?.unAttestedScript}
                  </p>
                </div>
                <div id="reader-checkbox-container" className="my-4 flex w-full flex-col">
                  {patientInfo?.navType.base === 'self' && (
                    <Checkbox
                      id="reader-checkbox"
                      name="reader"
                      onChange={() => {
                        handleReaderCheckboxClick();
                      }}
                      checked={hasReader}
                      label={`I am unable to read the script myself. I have asked someone to read it for me today.`}
                    />
                  )}
                  {hasReader && patientInfo?.navType.base === 'self' && (
                    <div id="add-reader-form-container" className="flex flex-col pl-4">
                      <h2 className="text-xl font-bold">
                        NOTE: When filming with a reader, the patient should introduce the reader if possible. The
                        patient must also be visible for the duration of the recording. This does not apply if the
                        person reading for the patient is the patient&apos;s legally appointed healthcare agent.
                      </h2>
                      <form id="add-reader-form" className="flex flex-col">
                        <addReaderForm.Field
                          name="fullName"
                          validators={{
                            onChange({ value }) {
                              if (!value || value.length === 0) {
                                return "Reader's name cannot be blank.";
                              } else {
                                return undefined;
                              }
                            },
                          }}
                          children={(field) => (
                            <>
                              <FormInput
                                label="Full Name*"
                                id="reader-full-name"
                                name={field.name}
                                value={field.state.value}
                                handleBlur={field.handleBlur}
                                handleChange={(e) => {
                                  setReaderSubmitted(false);
                                  field.handleChange(e.target.value);
                                }}
                                required={true}
                              />
                              <FieldInfo fieldMeta={field.state.meta} />
                            </>
                          )}
                        />
                        <addReaderForm.Field
                          name="email"
                          validators={{
                            onChange({ value }) {
                              if (!value || value.length === 0) {
                                return "Reader's email address cannot be blank.";
                              } else {
                                return undefined;
                              }
                            },
                          }}
                          children={(field) => (
                            <>
                              <FormInput
                                label="Email*"
                                id="reader-email"
                                name={field.name}
                                value={field.state.value}
                                handleBlur={field.handleBlur}
                                handleChange={(e) => {
                                  setReaderSubmitted(false);
                                  field.handleChange(e.target.value);
                                }}
                                required={true}
                              />
                              <FieldInfo fieldMeta={field.state.meta} />
                            </>
                          )}
                        />
                        <div className="mt-2">
                          <h2 className="text-xl font-bold">Reader&apos;s Cell Phone*</h2>
                          <addReaderForm.Field
                            name="phoneNumber"
                            validators={{
                              onChange: ({ value }) => {
                                if (!value || value.length === 0) {
                                  return "Reader's cell phone cannot be empty";
                                } else if (value.length < 11) {
                                  return "Reader's Cell Phone must contain at least 10 digits";
                                } else {
                                  return undefined;
                                }
                              },
                            }}
                            children={(field) => (
                              <>
                                <PhoneInput
                                  country={'us'}
                                  inputStyle={{ backgroundColor: '#374151', color: 'white', fontWeight: 'bold' }}
                                  inputProps={{
                                    name: 'readerPhone',
                                    required: true,
                                    // autoFocus: true,
                                  }}
                                  placeholder="Enter phone number"
                                  value={field.state.value ? field.state.value : ''}
                                  onChange={(value) => {
                                    if (!value || value.length === 0) {
                                      field.setValue('');
                                    }
                                    if (value) {
                                      setReaderSubmitted(false);
                                      field.setValue(value);
                                    }
                                  }}
                                />
                                <FieldInfo fieldMeta={field.state.meta} />
                              </>
                            )}
                          />
                          <div id="reader-id-container" className="mt-4 flex flex-col">
                            <p className="text-2xl font-bold">Reader ID Front</p>
                            {chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI &&
                            takingPhoto.readerIdentificationFront === false ? (
                              <div className="flex flex-row items-center">
                                <img
                                  src={chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI}
                                  style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                                  alt="Reader ID Front"
                                />
                                <div className="ml-4">
                                  <Button
                                    id="retake-reader-id-front"
                                    type="button"
                                    textContent="Retake Photo"
                                    onClick={() => {
                                      setTakingPhoto({ ...takingPhoto, readerIdentificationFront: true });
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              takingPhoto.readerIdentificationFront === false && (
                                <Button
                                  id="take-reader-id-front"
                                  type="button"
                                  textContent="Take Photo"
                                  onClick={() => {
                                    setTakingPhoto({ ...takingPhoto, readerIdentificationFront: true });
                                  }}
                                />
                              )
                            )}
                            {takingPhoto.readerIdentificationFront && (
                              <PhotoCamera
                                type={FileTypes.readerIdFront}
                                docToUpdate="SCRIPT_REVIEW"
                                style={'card'}
                                bucket={'patient'}
                                path={`user/${patientId}/images/readerIdentificationFront`}
                                patientId={patientId}
                                hideCamera={() => {
                                  setPhotoOneWorking(false);
                                  setTakingPhoto({ ...takingPhoto, readerIdentificationFront: false });
                                }}
                                working={photoOneWorking}
                                setWorking={setPhotoOneWorking}
                              />
                            )}
                          </div>
                          <div className="mt-8">
                            <addReaderForm.Subscribe
                              selector={(state) => [state.canSubmit, state.errors]}
                              children={([canSubmit]) => (
                                <>
                                  {!canSubmit && (
                                    <div className="mt-4 flex flex-col items-center">
                                      <p className="text-red-500">
                                        Please correct any errors above before re-submitting.
                                      </p>
                                    </div>
                                  )}
                                  {!readerSubmitted && (
                                    <Button
                                      id="submit-reader-info"
                                      type="submit"
                                      disabled={!canSubmit || !chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI}
                                      onClick={() => {
                                        // setReaderSubmitted(true);
                                        addReaderForm.handleSubmit();
                                      }}
                                      submitted={readerSubmitted}
                                      textContent="Add Reader"
                                    />
                                  )}
                                </>
                              )}
                            />
                          </div>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
                <div id="attestation-checkbox-container" className="my-4 flex w-full flex-row justify-center">
                  <Checkbox
                    id="attestation-checkbox"
                    name="attestation"
                    onChange={() => {
                      handleAttestationCheckboxClick();
                    }}
                    checked={attested}
                    label={`I attest that I have reviewed and approve of this script
                    ${patientInfo.navType.base !== 'self' ? 'including any edits' : ''} ${chart.SCRIPT_REVIEW?.speaker !== 'patient' ? "and the selected treatment choice. Once attested, a reader's script will be shown if requested." : ''}`}
                  />
                </div>
              </>
            </div>
          </div>
          {attested === false && patientInfo?.navType?.base === 'self' && (
            <Button
              id="report-error"
              type="button"
              textContent="There is an error in my script"
              onClick={() => setScripHasError(!scripHasError)}
              disabled={false}
            />
          )}
          {scripHasError && attested === false && (
            <>
              <form id="error-report-form" className="flex flex-col">
                <scriptErrorForm.Field
                  name="firstName"
                  validators={{
                    onChange({ value }) {
                      if (!value || value.length === 0) {
                        return 'First name cannot be blank.';
                      } else {
                        return undefined;
                      }
                    },
                  }}
                  children={(field) => (
                    <>
                      <FormInput
                        label="First Name*"
                        id="first-name"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={true}
                      />
                      <FieldInfo fieldMeta={field.state.meta} />
                    </>
                  )}
                />
                <scriptErrorForm.Field
                  name="middleInitial"
                  children={(field) => (
                    <>
                      <FormInput
                        label="Middle Initial"
                        id="middle-initial"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={false}
                      />
                      <FieldInfo fieldMeta={field.state.meta} />
                    </>
                  )}
                />
                <scriptErrorForm.Field
                  name="lastName"
                  validators={{
                    onChange({ value }) {
                      if (!value || value.length === 0) {
                        return 'Last name cannot be blank.';
                      } else {
                        return undefined;
                      }
                    },
                  }}
                  children={(field) => (
                    <>
                      <FormInput
                        label="Last Name*"
                        id="last-name"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={true}
                      />
                      <FieldInfo fieldMeta={field.state.meta} />
                    </>
                  )}
                />
                <scriptErrorForm.Field
                  name="DOB"
                  validators={{
                    onChange: ({ value }) => {
                      if (differenceInYears(Date.now(), new Date(value)) < 18) {
                        return '*Patient must be at least 18 years old to receive services';
                      }
                      if (!value) {
                        return 'Date of Birth is invalid';
                      }
                      return undefined;
                    },
                  }}
                  children={(field) => (
                    <div className="scheme-dark">
                      <FormInput
                        label="DOB* (MM/DD/YYYY)"
                        id="dob"
                        type="date"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={true}
                      />
                      <p className="text-xl font-bold text-green-900 italic">
                        *Please confirm patient DOB matches government ID before submitting.
                      </p>
                      <FieldInfo fieldMeta={field.state.meta} />
                    </div>
                  )}
                />
                <scriptErrorForm.Field
                  name="agent"
                  validators={{
                    onChange({ value }) {
                      if (!value || value.length === 0) {
                        return "Agent's name cannot be blank.";
                      } else {
                        return undefined;
                      }
                    },
                  }}
                  children={(field) => (
                    <>
                      <FormInput
                        label="Agent's Full Name*"
                        id="agent-name"
                        name={field.name}
                        value={field.state.value ? field.state.value : ''}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={true}
                      />
                      <FieldInfo fieldMeta={field.state.meta} />
                    </>
                  )}
                />
                <scriptErrorForm.Field
                  name="agentToPatient"
                  validators={{
                    onChange({ value }) {
                      if (!value || value.length === 0) {
                        return "Agent's relationship to patient cannot be blank.";
                      } else {
                        return undefined;
                      }
                    },
                  }}
                  children={(field) => (
                    <>
                      <FormInput
                        label="Agent's Relationship To Patient*"
                        id="agent-to-patient"
                        placeholder='This person is my "___"'
                        name={field.name}
                        value={field.state.value ? field.state.value : ''}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={true}
                      />
                      <FieldInfo fieldMeta={field.state.meta} />
                    </>
                  )}
                />
                <div className="mt-2">
                  <h2 className="text-xl font-bold">Agent&apos;s Cell Phone*</h2>
                  <scriptErrorForm.Field
                    name="agentCellPhone"
                    validators={{
                      onChange: ({ value }) => {
                        if (!value || value.length === 0) {
                          return "Reader's cell phone cannot be empty";
                        } else if (value.length < 11) {
                          return "Reader's Cell Phone must contain at least 10 digits";
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <PhoneInput
                          country={'us'}
                          inputStyle={{ backgroundColor: '#374151', color: 'white', fontWeight: 'bold' }}
                          inputProps={{
                            name: 'cellPhone',
                            required: true,
                            autoFocus: true,
                          }}
                          placeholder="Enter phone number"
                          value={field.state.value ? field.state.value : ''}
                          onChange={(value) => {
                            if (!value || value.length === 0) {
                              field.setValue('');
                            }
                            if (value) {
                              field.setValue(value);
                            }
                          }}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                </div>
                <div className="mt-8 flex flex-col items-center">
                  <scriptErrorForm.Subscribe
                    selector={(state) => [state.canSubmit, state.errors]}
                    children={([canSubmit]) => (
                      <>
                        {!canSubmit && (
                          <div className="mt-4 flex flex-col items-center">
                            <p className="text-red-500">Please correct any errors above before submitting.</p>
                          </div>
                        )}
                        <Button
                          id="submit-update-info"
                          type="submit"
                          disabled={!canSubmit}
                          onClick={() => {
                            scriptErrorForm.handleSubmit();
                          }}
                          textContent="Update Script"
                        />
                      </>
                    )}
                  />
                </div>
              </form>
            </>
          )}
          <div id="nav" className="flex flex-col items-center">
            {patientInfo?.navType.base === 'self' && (
              <Button
                id="submit-info"
                type="button"
                textContent="Submit"
                onClick={() => setIsSubmitted(true)}
                disabled={
                  !chart?.SCRIPT_REVIEW?.telePrompterScript ||
                  !attested ||
                  (hasReader && (!readerSubmitted || !chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI))
                }
                submitted={isSubmitted}
              />
            )}
            {nextPageURL ? (
              attested && patientInfo.navType.base === 'self' ? (
                <NavForward to={nextPageURL} disabled={!attested || !isSubmitted} />
              ) : (
                <NavForward to={nextPageURL} disabled={!attested} />
              )
            ) : null}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
